import { useEffect, useState } from 'react'
import { Icon } from './Icon'
import { FieldHelp } from './FieldHelp'
import { useEscape } from '../useEscape'
import {
  api,
  isBlocking,
  type Frontmatter,
  type FrontmatterPatch,
  type Member,
  type Problem,
} from '../api'
import { timeAgo } from '../time'
import { isSiteFile, type SiteFile } from '@staleless/schema'

/**
 * The right rail: what this page IS, as opposed to what it says.
 *
 * Frontmatter is fields rather than YAML text, and each one commits on blur —
 * saving goes through the parser server-side, so editing a title reserialises
 * the file canonically instead of letting the form's key order reach disk.
 */
/**
 * The four cadences people choose, in days.
 *
 * A month, a quarter, half a year, a year. Not a rule — `review_every` takes
 * any number and the menu offers Custom — but a menu of the common answers is
 * the difference between a feature somebody uses and a number field that asks
 * them to invent something before they can start.
 */
const CADENCES = [30, 90, 180, 365]

/**
 * What this rail says about each file that is not a page.
 *
 * It said it about `nav.yaml` and about nothing else, so the other five site
 * files fell through to the PAGE form below — a title, a description, an
 * owner, a review cadence — offered for a file that has none of those. The
 * fields were not decorative: each commits on blur through
 * `PUT /api/frontmatter`, which parses the file and writes it back, so a title
 * typed here prepended `---` to `search.yaml` and canonicalised the YAML under
 * it as prose. The route refuses that now; this stops the fields being drawn
 * at all, which is the half a person meets.
 *
 * A `Record` over the type, so a sixth site file cannot compile until somebody
 * has written the sentence that says what it is.
 */
const SITE_FILE_PANELS: Record<SiteFile, { label: string; prose: string[] }> = {
  'nav.yaml': {
    label: 'Navigation',
    prose: [
      'Ordering for the whole site, in one file. Editing it here is the same as reordering the published sidebar.',
      'A page that is not listed is an orphan, and the commit will be refused until it is.',
    ],
  },
  'variables.yaml': {
    label: 'Variables',
    prose: [
      'Values pages reference as {{name}}, in one file. Change a product name here and every page that names it changes with the draft.',
      'A reference nothing defines renders as literal braces to a reader, which the linter warns about rather than refusing.',
    ],
  },
  'search.yaml': {
    label: 'Search',
    prose: [
      'What a reader\u2019s search finds: the words that mean the same thing, the ones that mean another, and the pages pinned or weighted for a query.',
      'Tuning changes the order things are found in. It never removes a page from the results.',
    ],
  },
  'redirects.yaml': {
    label: 'Redirects',
    prose: [
      'The addresses this site keeps working that are not about one page \u2014 what a migration brings, a whole section that used to live somewhere else.',
      'A redirect is matched before the filesystem, so a rule naming a published page\u2019s address takes that page off the site. The linter refuses that.',
    ],
  },
  'glossary.yaml': {
    label: 'Words',
    prose: [
      'The words this documentation uses, and the ones it replaces. Every page is checked against them, and a reader who types a discouraged word still arrives.',
      'A file nobody can parse stops the checking silently, so the linter treats that as an error.',
    ],
  },
  'style.yaml': {
    label: 'Style',
    prose: [
      'How this documentation is written: the voice to keep, and the habits to avoid. Suggestions are written against it.',
      'There is nothing here to check a page against \u2014 a style is a judgement. A file that will not parse simply stops guiding, which is worth saying.',
    ],
  },
}
const LABEL_FOR: Record<number, string> = {
  30: 'Every month',
  90: 'Every quarter',
  180: 'Every six months',
  365: 'Every year',
}

interface Props {
  path: string
  /** The draft being read, so the history is the one this page has HERE. */
  branch: string | null
  frontmatter: Frontmatter | null
  problems: Problem[]
  /** Who could own this page. Empty while they are still being fetched. */
  people: Member[]
  disabled: boolean
  onSave: (patch: FrontmatterPatch) => Promise<void>
  /** Move the page. Rejects with the reason, which is drawn where it was asked. */
  onRename: (to: string) => Promise<void>
  /**
   * Move the page to the trash.
   *
   * Resolves with `null` when it is gone, and with the pages that link to it
   * when the server refused — which is a question, not a failure: unlike a
   * rename there is nowhere to repoint those links, so somebody has to decide,
   * and they should not decide without reading which pages break.
   */
  onDelete: (force: boolean) => Promise<string[] | null>
}

export function Inspector({
  path,
  branch,
  frontmatter,
  problems,
  people,
  disabled,
  onSave,
  onRename,
  onDelete,
}: Props) {
  const [draft, setDraft] = useState<Frontmatter | null>(frontmatter)
  const [saving, setSaving] = useState(false)
  /** True while somebody is typing a cadence the menu does not offer. */
  const [custom, setCustom] = useState(false)
  /**
   * The old addresses as one line of text, so a half-typed list is not saved.
   *
   * A comma-separated field rather than rows with an Add button: this is
   * edited once, at a migration, and a list of two things does not need a
   * list editor.
   */
  const [oldAddresses, setOldAddresses] = useState('')
  /**
   * The address being typed while a rename is open, null while it is not.
   *
   * Both of these were the operating system's boxes — `window.prompt` for the
   * new address, `window.confirm` twice for the delete. A native box cannot be
   * styled, covers the panel it is asking about, holds no rule, and is
   * answered by anything driving the page with a dismissal, so none of this
   * had ever been driven.
   */
  const [renamingTo, setRenamingTo] = useState<string | null>(null)
  /**
   * What the delete is waiting on: the first question, or the pages that link
   * here after the server refused. Null while nothing is being deleted.
   */
  const [deleting, setDeleting] = useState<'asking' | { linked: string[] } | null>(null)
  const [busy, setBusy] = useState(false)
  /** A refused rename or delete, said where it was asked. */
  const [actError, setActError] = useState<string | null>(null)

  /**
   * Escape backs out of the question, not out of the page.
   *
   * The same rule the trash drawer already follows: a person who opened a
   * rename and changed their mind presses Escape, and nothing else on the
   * screen should move.
   */
  useEscape(renamingTo !== null || deleting !== null, () => {
    setRenamingTo(null)
    setDeleting(null)
    setActError(null)
  })

  /** One act, with the panel busy while it runs and its refusal kept here. */
  const act = async (run: () => Promise<void>) => {
    setBusy(true)
    setActError(null)
    try {
      await run()
    } catch (e) {
      setActError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    setDraft(frontmatter)
    // A different page is a different question. Leaving a half-typed rename or
    // an open confirmation behind would aim it at whatever is open now.
    setRenamingTo(null)
    setDeleting(null)
    setActError(null)
    // A page whose cadence is not one of the four opens with the number
    // showing, rather than with a menu that quietly disagrees with the file.
    const days = frontmatter?.['review_every']
    setCustom(typeof days === 'number' && !CADENCES.includes(days))
    const from = frontmatter?.['redirect_from']
    setOldAddresses(Array.isArray(from) ? from.join(', ') : '')
  }, [frontmatter, path])

  /**
   * Nothing is open.
   *
   * This used to fall into the "still loading" branch below, which draws two
   * skeleton boxes — a shape that promises content is on its way. With no page
   * selected, nothing is coming: no request is in flight and none will be
   * fired until somebody picks a page. The skeleton then just sits there,
   * shimmering forever, which reads as broken rather than as an empty rail. It
   * says what the panel is for instead, same as the main pane's own empty
   * state.
   */
  if (!path) {
    return (
      <aside className="inspector">
        <div className="inspector__head">
          <span className="label">Page</span>
        </div>
        <div className="inspector__empty">
          <span className="inspector__empty-icon">
            <Icon name="pages" size={16} />
          </span>
          <p className="inspector__empty-text">
            Title, placement and freshness for the page you have open. Pick one to see them here.
          </p>
        </div>
      </aside>
    )
  }

  if (isSiteFile(path)) {
    const says = SITE_FILE_PANELS[path as SiteFile]
    return (
      <aside className="inspector">
        <div className="inspector__head">
          <span className="label">{says.label}</span>
        </div>
        <div className="inspector__section">
          {says.prose.map((line) => (
            <p className="inspector__prose" key={line}>
              {line}
            </p>
          ))}
        </div>
        <Problems problems={problems} />
      </aside>
    )
  }

  if (!draft) {
    // A page is open but its details have not arrived yet. Unlike the case
    // above, something IS coming, so the skeleton is honest here — it still
    // names what it is waiting for, per BRAND.md 7: "text says what is
    // happening."
    return (
      <aside className="inspector">
        <div className="inspector__head">
          <span className="label">Page</span>
        </div>
        {/* The same treatment the six screens use, rather than two bars whose
            heights and gap were written as raw pixels in a style prop — the
            only three numbers in the panel that were not on the space scale,
            in the one place the app already had a rule for. */}
        <div className="inspector__section">
          <p className="field__note">Loading this page&rsquo;s details…</p>
          <div className="screen__loading" aria-busy="true">
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        </div>
      </aside>
    )
  }

  /**
   * Change one field in the draft.
   *
   * `null` is how a field is CLEARED, and it has to travel as null all the way
   * to the server: `JSON.stringify` drops a key whose value is `undefined`, so
   * a clear written that way never left the browser and the old value came
   * back on the next load. The draft holds the null, `commit` sends it, and
   * the route deletes the key.
   */
  const set = <K extends keyof Frontmatter>(key: K, value: Frontmatter[K] | null) =>
    setDraft({ ...draft, [key]: value === null ? null : value } as Frontmatter)

  /** What the page says, whether or not that person is still here. */
  const owner = String(draft['owner'] ?? '')
  const cadence = draft['review_every'] as number | undefined

  const commit = async () => {
    if (disabled || !draft) return
    setSaving(true)
    try {
      await onSave(draft as FrontmatterPatch)
    } finally {
      setSaving(false)
    }
  }

  return (
    <aside className="inspector">
      <div className="inspector__head">
        <span className="label">Page</span>
        {saving && (
          <span className="badge badge--accent">
            <Icon name="spinner" size={11} /> saving
          </span>
        )}
      </div>

      <div className="inspector__section">
        <label className="field">
          <span className="label">
            Title
            <FieldHelp
              label="the title"
              says="The heading at the top of the page, the link in search results, and what the navigation lists it under unless you rename the row itself. Changing it does not change the page's address."
            />
          </span>
          <input
            className="input"
            value={draft.title ?? ''}
            disabled={disabled}
            onChange={(e) => set('title', e.target.value)}
            onBlur={commit}
          />
        </label>

        <label className="field">
          <span className="label">
            Description
            <FieldHelp
              label="the description"
              says="One sentence, shown under the title in search results and on any card that links here. Readers decide whether to open the page on this, so it is worth writing rather than leaving to a machine."
            />
          </span>
          <textarea
            className="textarea"
            rows={3}
            placeholder="One sentence. It is the search result and the card subtitle."
            value={draft.description ?? ''}
            disabled={disabled}
            onChange={(e) => set('description', e.target.value)}
            onBlur={commit}
          />
        </label>
      </div>

      <div className="inspector__section">
        <div className="label inspector__section-label">Placement</div>
        <label className="field">
          <span className="label">
            Section
            <FieldHelp
              label="the section"
              says="A note on the page saying where it belongs. It does NOT move the page: the sidebar's order comes from the navigation, which you change by dragging in the tree."
            />
          </span>
          <input
            className="input"
            value={draft.section ?? ''}
            disabled={disabled}
            onChange={(e) => set('section', e.target.value)}
            onBlur={commit}
          />
        </label>
        <div className="inspector__row">
          <label className="field">
            <span className="label">
              Group
              <FieldHelp
                label="the group"
                says="The heading this page sits under inside its section, as a note on the page. Like Section, it records an intention — the tree is what readers actually get."
              />
            </span>
            <input
              className="input"
              value={draft.group ?? ''}
              disabled={disabled}
              onChange={(e) => set('group', e.target.value)}
              onBlur={commit}
            />
          </label>
          <label className="field inspector__narrow">
            <span className="label">
              Order
              <FieldHelp
                label="the order"
                says="A number an importer usually wrote. Nothing on the published site reads it — the navigation decides the order — so it is kept only because throwing away somebody's imported metadata is worse than showing it."
              />
            </span>
            <input
              className="input"
              type="number"
              value={draft.order ?? ''}
              disabled={disabled}
              onChange={(e) =>
                set('order', e.target.value === '' ? undefined : Number(e.target.value))
              }
              onBlur={commit}
            />
          </label>
        </div>
        <p className="field__note">
          Placement here is metadata. The published order comes from the navigation file.
        </p>
      </div>

      <div className="inspector__section">
        <div className="label inspector__section-label">
          Freshness
          <FieldHelp
            label="freshness"
            says="How long this page can go unchecked before Pulse asks about it. Marking it reviewed is a promise about today: it resets the clock and changes nothing on the page."
          />
        </div>
        {/*
          The product is named after this. `last_reviewed` and `review_every` are
          what the health assessment reads, they survive a save, and the site
          build shows them — and there was no way to set either, so the whole
          feature ran on whatever the importer happened to write.

          A button, not a date field, for the common case. "I have just checked
          this page against the product" is one action, and asking somebody to
          type today's date in the right format is how a feature stops being used.
        */}
        <div className="inspector__row">
          <button
            className="btn btn--sm btn--outline"
            disabled={disabled}
            onClick={() => {
              // Local date, not UTC: somebody reviewing a page at 23:00 in
              // Sydney did it today, and `toISOString` would say tomorrow.
              const now = new Date()
              const today = [
                now.getFullYear(),
                String(now.getMonth() + 1).padStart(2, '0'),
                String(now.getDate()).padStart(2, '0'),
              ].join('-')
              setDraft({ ...draft, last_reviewed: today })
              void onSave({ ...draft, last_reviewed: today })
            }}
          >
            <Icon name="check" size={13} />
            Reviewed today
          </button>
          {draft['last_reviewed'] ? (
            <span className="field__note">Last checked {String(draft['last_reviewed'])}</span>
          ) : (
            <span className="field__note">Never marked as checked</span>
          )}
        </div>
        {/*
          A menu of the four cadences people actually choose, plus custom.

          It was a bare number field, which asks somebody to invent a number
          before they can use the feature — and the four that get chosen are
          a month, a quarter, half a year and a year. Custom stays, because a
          release train on a six-week cycle is a real answer and a closed list
          would be us telling a customer their cadence is wrong.
        */}
        <label className="field">
          <span className="label">Recheck</span>
          <select
            className="select"
            aria-label="Recheck"
            value={custom ? 'custom' : String(cadence ?? '')}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setCustom(true)
                return
              }
              setCustom(false)
              const days = e.target.value === '' ? null : Number(e.target.value)
              setDraft({ ...draft, review_every: days ?? undefined })
              void onSave({ ...draft, review_every: days })
            }}
          >
            <option value="">When it looks old — no fixed cadence</option>
            {CADENCES.map((days) => (
              <option key={days} value={days}>
                {LABEL_FOR[days]}
              </option>
            ))}
            <option value="custom">Every… (choose the days)</option>
          </select>
        </label>
        {custom && (
          <label className="field">
            <span className="label">Days between checks</span>
            <input
              className="input"
              type="number"
              min={1}
              autoFocus
              value={(draft['review_every'] as number | undefined) ?? ''}
              disabled={disabled}
              onChange={(e) =>
                set('review_every', e.target.value === '' ? null : Number(e.target.value))
              }
              onBlur={commit}
            />
          </label>
        )}
        <p className="field__note">
          Pages age at different rates. A pricing page goes stale in a month; an overview can be
          right for years.
        </p>
        {/*
          Who to ask when it goes stale.

          A list of the people who are actually here, not a free text field:
          the address has to resolve to a member for anything to be able to
          tell them, and typing one by hand is how it ends up misspelled.

          An owner who has since LEFT still appears in this list — as their own
          address, marked — because otherwise opening the page would silently
          reassign it to nobody, and the fact worth seeing is exactly that the
          person who looked after this page is gone.
        */}
        <label className="field">
          <span className="label">Owner</span>
          <select
            className="select"
            aria-label="Owner"
            value={String(draft['owner'] ?? '')}
            disabled={disabled}
            onChange={(e) => {
              // `null`, not `undefined`: undefined is dropped by
              // JSON.stringify, so "Nobody yet" would never reach the server.
              const next = e.target.value === '' ? null : e.target.value
              setDraft({ ...draft, owner: next ?? undefined })
              void onSave({ ...draft, owner: next })
            }}
          >
            <option value="">Nobody yet</option>
            {people.map((person) => (
              <option key={person.userId} value={person.email}>
                {person.displayName} ({person.email})
              </option>
            ))}
            {owner !== '' && !people.some((p) => p.email === owner) && (
              <option value={owner}>{owner} — no longer a member</option>
            )}
          </select>
        </label>
        <p className="field__note">
          {owner === ''
            ? 'Nobody is named for this page, so a review has nobody to go to.'
            : 'Who gets asked when this page needs checking.'}
        </p>
      </div>

      {/*
        Collapsed, because most pages never need any of it — and an inspector
        that shows every field at once is one nobody reads to the bottom of.
      */}
      <details className="inspector__section inspector__more">
        <summary className="label inspector__section-label">Search and sharing</summary>
        <label className="field">
          <span className="label">Title for search</span>
          <input
            className="input"
            placeholder="Only when the on-page heading is not the best query"
            value={(draft['seo_title'] as string | undefined) ?? ''}
            disabled={disabled}
            onChange={(e) => set('seo_title', e.target.value || null)}
            onBlur={commit}
          />
        </label>
        <label className="field">
          <span className="label">Description for search</span>
          <textarea
            className="textarea"
            rows={2}
            placeholder="Overrides the description in meta tags only"
            value={(draft['seo_description'] as string | undefined) ?? ''}
            disabled={disabled}
            onChange={(e) => set('seo_description', e.target.value || null)}
            onBlur={commit}
          />
        </label>
        <label className="field">
          <span className="label">Social card image</span>
          <input
            className="input"
            placeholder="/assets/…"
            value={(draft['social_image'] as string | undefined) ?? ''}
            disabled={disabled}
            onChange={(e) => set('social_image', e.target.value || null)}
            onBlur={commit}
          />
        </label>
        <label className="field">
          <span className="label">Canonical URL</span>
          <input
            className="input"
            placeholder="https://…"
            value={(draft['canonical'] as string | undefined) ?? ''}
            disabled={disabled}
            onChange={(e) => set('canonical', e.target.value || null)}
            onBlur={commit}
          />
        </label>
        <p className="field__note">
          For content published somewhere else first. Setting it wrongly takes this page out of
          search entirely.
        </p>

        <label className="inspector__check">
          <input
            type="checkbox"
            checked={draft['noindex'] === true}
            disabled={disabled}
            onChange={(e) => {
              const next = { ...draft, noindex: e.target.checked ? true : undefined }
              setDraft(next)
              void onSave(next)
            }}
          />
          <span>
            Keep out of search
            <span className="field__note">
              Still public and reachable by link — for a page you send people to directly.
            </span>
          </span>
        </label>

        {/*
          §22's marker, and the way to clear it.
          Only shown when it is SET: a checkbox offering to declare a page
          model-written would be a strange thing to hand somebody, and nothing
          in the product sets it except accepting a suggestion. Clearing it is a
          person saying they have read the words, which is what "until reviewed"
          means.
        */}
        {draft['ai_draft'] === true && (
          <label className="inspector__check" data-ai-draft>
            <input
              type="checkbox"
              checked
              disabled={disabled}
              onChange={() => {
                // `null`, not `undefined`, or the clear never leaves the
                // browser — the same rule `set` above is written for.
                const next = { ...draft, ai_draft: null } as unknown as Frontmatter
                setDraft(next)
                void onSave(next as FrontmatterPatch)
              }}
            />
            <span>
              Carries an unreviewed suggestion
              <span className="field__note">
                A model wrote some of these words and nobody has said they read them. Untick it when
                you have.
              </span>
            </span>
          </label>
        )}

        <label className="inspector__check">
          <input
            type="checkbox"
            checked={draft['draft'] === true}
            disabled={disabled}
            onChange={(e) => {
              const next = { ...draft, draft: e.target.checked ? true : undefined }
              setDraft(next)
              void onSave(next)
            }}
          />
          <span>
            Draft
            <span className="field__note">
              Not published at all. Different from keeping it out of search.
            </span>
          </span>
        </label>
      </details>

      <div className="inspector__section">
        <div className="label inspector__section-label">File</div>
        {/*
          The address, and the address being changed, in the same place.

          A `window.prompt('New path', path)` used to ask this: a box the
          operating system drew OVER the address it was about, carrying no
          sentence about what a rename does to everything pointing here — and
          the panel's own fields, one of which is literally the list of old
          addresses a rename writes, were three lines below it.
        */}
        {renamingTo === null ? (
          <code className="inspector__path">{path}</code>
        ) : (
          <form
            className="inspector__rename"
            onSubmit={(e) => {
              e.preventDefault()
              void act(async () => {
                const to = renamingTo.trim()
                if (to === '' || to === path) return
                await onRename(to)
                setRenamingTo(null)
              })
            }}
          >
            <label className="label" htmlFor="inspector-rename">
              Move it to
            </label>
            <input
              id="inspector-rename"
              className="input mono"
              autoFocus
              value={renamingTo}
              disabled={busy}
              aria-describedby="inspector-rename-note"
              onChange={(e) => setRenamingTo(e.target.value)}
            />
            <p className="field__note" id="inspector-rename-note">
              Every link to this page is repointed, the navigation follows, and the old address
              redirects — so nobody&rsquo;s bookmark breaks.
            </p>
            <div className="inspector__actions">
              <button
                className="btn btn--sm btn--tinted"
                disabled={busy || renamingTo.trim() === '' || renamingTo.trim() === path}
              >
                <Icon name="rename" size={13} />
                Rename
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                disabled={busy}
                onClick={() => setRenamingTo(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/*
          The addresses this page used to have.

          A rename writes one of these automatically, and until now that was
          the ONLY way one could appear: the field was in the schema, enforced
          by lint, built into `_redirects` and honoured at the edge, with no
          way for a person to add one. An address that was never ours — a page
          from the site this replaced — had nowhere to be typed.
        */}
        <label className="field">
          <span className="label">Old addresses</span>
          <input
            className="input mono"
            placeholder="/docs/old-name, /old-name"
            value={oldAddresses}
            disabled={disabled}
            onChange={(e) => setOldAddresses(e.target.value)}
            onBlur={() => {
              const list = oldAddresses
                .split(',')
                .map((one) => one.trim())
                .filter((one) => one !== '')
              const next = { ...draft, redirect_from: list.length === 0 ? null : list }
              setDraft({ ...draft, redirect_from: list.length === 0 ? undefined : list })
              void onSave(next)
            }}
          />
        </label>
        <p className="field__note">
          Anyone arriving at one of these lands here instead. Moving a page adds one for you; add
          one by hand when the address was never ours — a page from the site this replaced.
        </p>

        {actError && (
          <p className="panel__error" role="alert">
            {actError}
          </p>
        )}

        {/*
          Deleting, asked here rather than by `window.confirm`.

          Two of those stood in this path. The first asked "Move X to the
          trash?" in a box over the panel, saying nothing about the trash being
          a place a page comes back from — which is the entire difference
          between this and losing the work. The second arrived only when the
          server refused, and printed the pages that link here as a wall of
          text inside an alert: unscrollable, unstyled, and gone the moment it
          was answered, so a writer deciding whether to break nine pages could
          not read the list while deciding.
        */}
        {/* While one of the two questions is open it IS the section's action.
            Leaving the row underneath drew a second Rename, and a Move to
            trash that would have opened a second question over the first. */}
        {renamingTo !== null ? null : deleting === null ? (
          <div className="inspector__actions">
            <button
              className="btn btn--sm btn--outline"
              disabled={disabled}
              onClick={() => {
                setActError(null)
                setRenamingTo(path)
              }}
            >
              <Icon name="rename" size={13} />
              Rename
            </button>
            <button
              className="btn btn--sm btn--danger"
              disabled={disabled}
              onClick={() => {
                setActError(null)
                setRenamingTo(null)
                setDeleting('asking')
              }}
            >
              <Icon name="trash" size={13} />
              Move to trash
            </button>
          </div>
        ) : deleting === 'asking' ? (
          <div className="inspector__confirm">
            <p className="field__note">
              It goes to the trash, where it can be restored. It leaves the navigation, and it stays
              out of the site until you put it back.
            </p>
            <div className="inspector__actions">
              <button
                className="btn btn--sm btn--danger"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    const linked = await onDelete(false)
                    // Refused, and the reason is a list somebody has to read:
                    // it becomes the next question rather than a dismissed box.
                    setDeleting(linked === null ? null : { linked })
                  })
                }
              >
                <Icon name="trash" size={13} />
                Move to trash
              </button>
              <button
                className="btn btn--sm btn--ghost"
                disabled={busy}
                onClick={() => setDeleting(null)}
              >
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <div className="inspector__confirm">
            <p className="field__note">
              {deleting.linked.length} page{deleting.linked.length === 1 ? '' : 's'} link
              {deleting.linked.length === 1 ? 's' : ''} to this one. Unlike a rename there is
              nowhere to repoint them, so those links break:
            </p>
            <ul className="inspector__linked">
              {deleting.linked.map((one) => (
                <li key={one}>
                  <code>{one}</code>
                </li>
              ))}
            </ul>
            <div className="inspector__actions">
              <button
                className="btn btn--sm btn--danger"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await onDelete(true)
                    setDeleting(null)
                  })
                }
              >
                <Icon name="trash" size={13} />
                Break them and delete
              </button>
              <button
                className="btn btn--sm btn--ghost"
                disabled={busy}
                onClick={() => setDeleting(null)}
              >
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>

      <Problems problems={problems} />
      <Revisions path={path} branch={branch} />
    </aside>
  )
}

type Revision = Awaited<ReturnType<typeof api.log>>['commits'][number]

/**
 * Where a revision that was NOT typed here came from, in a person's words.
 *
 * `editor` is deliberately absent. Every row would carry it, and a badge on
 * every row is not a badge — it marks the exception, which is the whole
 * question this list answers: did somebody push straight to the repository,
 * did an importer write this, was it an agent, was it a rollback.
 */
const SOURCE_LABEL: Partial<Record<Revision['source'], string>> = {
  git: 'pushed to the repository',
  import: 'imported',
  rollback: 'rollback',
  api: 'by an agent',
}

/**
 * What happened to this page, and where each change came from.
 *
 * The workspace has kept this since commits carried a source trailer, and
 * nothing in the product ever asked for it: History shows PUBLICATIONS, which
 * is the site going out, not the page changing. So "who touched this page and
 * when" — the first question anybody asks of a document they did not write —
 * had no answer here. Found by auditing which client calls no screen makes.
 *
 * Read for the branch being edited, not for the default one: a draft's history
 * is the main line plus what this draft has committed on top, and answering
 * with main's would quietly disagree with the words on the screen.
 */
function Revisions({ path, branch }: { path: string; branch: string | null }) {
  const [commits, setCommits] = useState<Revision[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Guarded against a slow answer for a page nobody is looking at any more:
    // opening three pages quickly must not leave the third showing the first's
    // history.
    let live = true
    setCommits(null)
    setError(null)
    if (!path) return
    void api
      .log(branch ?? undefined, path)
      .then((answer) => {
        if (live) setCommits(answer.commits)
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      live = false
    }
  }, [path, branch])

  return (
    <div className="inspector__section" data-revisions>
      <div className="label inspector__section-label">History</div>
      {/* A read that failed, said as a failure. It was `.field__note` — the
          grey the panel uses for "reviewed 3 days ago" — so a history the
          server could not produce read as a remark about the page. */}
      {error ? (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      ) : !commits ? (
        /* And the section stays on the page while it reads. `return null`
           stood here, so History was absent from the inspector and then
           appeared at the bottom of it, moving nothing above it but arriving
           like a new panel each time a page was opened. */
        <div className="screen__loading" aria-busy="true">
          <p className="sr-only">Loading…</p>
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      ) : commits.length === 0 ? (
        <p className="nothing" data-revisions-empty>
          Nothing committed yet. This page appears here once a draft carrying it is committed.
        </p>
      ) : (
        <ul className="revisions">
          {commits.map((commit) => (
            <li key={commit.sha} className="revisions__row" data-revision>
              <span className="revisions__message">{commit.message}</span>
              <span className="revisions__meta">
                {commit.author} · {timeAgo(commit.date)}
                {SOURCE_LABEL[commit.source] && (
                  <>
                    {' · '}
                    <span className="badge">{SOURCE_LABEL[commit.source]}</span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Problems({ problems }: { problems: Problem[] }) {
  if (problems.length === 0) return null
  return (
    <div className="inspector__section">
      <div className="label inspector__section-label">Checks</div>
      {problems.map((p, i) => (
        <div key={i} className={`check${isBlocking(p) ? ' check--blocking' : ''}`}>
          <Icon name={isBlocking(p) ? 'alert' : 'info'} size={13} />
          <div>
            <div className="check__rule">{p.rule}</div>
            <div className="check__message">{p.message}</div>
          </div>
        </div>
      ))}
    </div>
  )
}
